<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    DocumentTemplatesService,
    type CustomerDocumentTemplate,
    type CustomerUser as User,
    type LawFields,
    LawFieldsService
  } from '@/types/generated'
  import { onMounted, ref, computed, watch } from 'vue'
  import { Button } from '@/components/ui/button'
  import * as yup from 'yup'
  import { useRouter } from 'vue-router'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import { SlashIcon, EyeIcon } from '@heroicons/vue/24/outline'
  import { useUserStore } from '@/shared/stores/userStore'
  import { debounce } from '@/lib/timing'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { CopyIcon } from 'lucide-vue-next'
  import { ROUTES } from '@/router/routes'

  const router = useRouter()
  const template = ref<CustomerDocumentTemplate>()
  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user)
  const lawFields = ref<LawFields | null>(null)
  const lawFieldOptions = computed<User>(() => useUserStore().lawFieldOptions)
  const lawTopicOptions = computed<Option[]>(() => {
    if (!template.value || !template.value.law_field) {
      return []
    }
    return (
      lawFields.value?.data[template.value.law_field]?.map((topic) => ({
        value: topic,
        title: topic
      })) || []
    ).sort((a, b) => a.value.localeCompare(b.value))
  })

  onMounted(async () => {
    const fetching = [fetchTemplate(), fetchLawFields()]
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
  })

  const fetchTemplate = async () => {
    template.value = await DocumentTemplatesService.documentTemplatesRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })
  }

  const fetchLawFields = async () => {
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  }

  const handleFormSave = async (values: any) => {
    if (!template.value || template.value?.is_jupus_template) return
    await DocumentTemplatesService.documentTemplatesPartialUpdate({
      uuid: template.value.uuid,
      requestBody: {
        ...values,
        name: template.value.name,
        document_template: template.value.document_template,
        description: template.value.description,
        system_prompt: template.value.system_prompt,
        law_field: template.value.law_field,
        law_topic: template.value.law_topic
      }
    })
  }

  // Watch for changes in rich text editor and sections
  watch([() => template.value?.document_template], () => {
    debounce(
      handleFormSave({
        document_template: template.value?.document_template
      }),
      1000
    )
  })

  const copyTemplate = async () => {
    const newTemplate = await DocumentTemplatesService.documentTemplatesCopyCreate({
      uuid: template.value.uuid
    })
    await router.push(
      ROUTES.APP.SETTINGS.DOCUMENT_GENERATION.TEMPLATE_DETAIL.replace(':uuid', newTemplate.uuid)
    )
  }
</script>
<template>
  <SettingsPage v-if="template && user && lawFields">
    <template #breadcrumbs-end>
      <li class="inline-flex items-center truncate text-sm font-semibold text-gray-800">
        <SlashIcon class="size-5 shrink-0 text-gray-500" />
        {{ template.name }}
      </li>
    </template>
    <template #actions>
      <div class="flex items-center space-x-2">
        <button
          @click="copyTemplate"
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
        >
          <CopyIcon class="h-5 w-5" />
          Duplizieren
        </button>
      </div>
    </template>
    <div v-if="template.is_jupus_template">
      <div class="mb-4 border-l-4 border-yellow-400 bg-yellow-50 p-4">
        <p class="text-sm font-semibold text-yellow-700">
          JUPUS Vorlagen können nicht direkt bearbeitet werden. Klicken Sie auf "Duplizieren" um
          eine bearbeitbare Kopie zu erstellen.
        </p>
      </div>
    </div>
    <AutoSavingForm @save="handleFormSave">
      <div class="mb-4">
        <InputField
          name="name"
          label="Name"
          v-model="template.name"
          :rules="yup.string().required()"
          :disabled="template.is_jupus_template"
        />
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <SelectField
            v-model="template.law_field"
            :options="lawFieldOptions"
            name="law_field"
            title="Rechtsgebiet"
            label="Rechtsgebiet"
            :allow-unselect="true"
            unselected-option-text="Alle Rechtsgebiete"
            :disabled="template.is_jupus_template"
          />
          <SelectField
            v-model="template.law_topic"
            :options="lawTopicOptions"
            name="law_topic"
            title="Rechtsthema"
            label="Rechtsthema"
            :allow-unselect="true"
            unselected-option-text="Alle Rechtsthemen"
            :disabled="template.is_jupus_template"
          />
        </div>
        <TextAreaField
          name="description"
          label="Beschreibung"
          v-model="template.description"
          :disabled="template.is_jupus_template"
          :rules="yup.string().required()"
        />
        <TextAreaField
          name="system_prompt"
          label="Systemprompt"
          v-model="template.system_prompt"
          :disabled="template.is_jupus_template"
          :rules="yup.string().required()"
        />
      </div>
    </AutoSavingForm>
    <RichTextEditor
      v-model="template.document_template"
      :enable-code-view-button="true"
      :disabled="template.is_jupus_template"
      :show-disabled-styling="true"
    />
  </SettingsPage>
</template>
